import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import {
  IdentitiesAccountStatus,
  IdentitiesAccountType,
  IdentitiesUserItemRes,
} from '../../apis/types';
import { SortOrder } from 'utils/sort-order/sort';

export interface IdentitiesState {
  accountType?: IdentitiesAccountType;
  accountStatus?: IdentitiesAccountStatus;
  activeSortedField: string;
  activeSortedOrder: SortOrder;
  userGroupsTeam?: Record<string, string>[];
  searchQuery: string;
  repoTableSearchQuery: string;
  memberTableSearchQuery: string;
  editRows: IdentitiesUserItemRes[];
  hoveredRow: any;
  identitiesDetailsTab: number;
}

const initialState: IdentitiesState = {
  accountType: IdentitiesAccountType.total,
  activeSortedField: 'access_count',
  activeSortedOrder: SortOrder.Desc,
  searchQuery: '',
  repoTableSearchQuery: '',
  memberTableSearchQuery: '',
  editRows: [],
  hoveredRow: '',
  identitiesDetailsTab: 0,
};

export const identitiesSlice = createSlice({
  name: 'identities',
  initialState,
  reducers: {
    setAccountType: (
      state: IdentitiesState,
      action: PayloadAction<IdentitiesAccountType | undefined>,
    ) => {
      state.accountType = action.payload;
    },
    setAccountStatus: (
      state: IdentitiesState,
      action: PayloadAction<IdentitiesAccountStatus | undefined>,
    ) => {
      state.accountStatus = action.payload;
    },
    setActiveSortedField: (
      state: IdentitiesState,
      action: PayloadAction<string>,
    ) => {
      state.activeSortedField =
        action.payload || initialState.activeSortedField;
    },
    setActiveSortedOrder: (
      state: IdentitiesState,
      action: PayloadAction<SortOrder>,
    ) => {
      state.activeSortedOrder = action.payload;
    },
    setUserGroupsTeams: (
      state: IdentitiesState,
      action: PayloadAction<Record<string, string>[]>,
    ) => {
      state.userGroupsTeam = action.payload;
    },
    setSearchQuery: (state: IdentitiesState, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    resetIdentitiesFilters: (state: IdentitiesState) => {
      state.searchQuery = initialState.searchQuery;
      state.repoTableSearchQuery = initialState.repoTableSearchQuery;
      state.memberTableSearchQuery = initialState.memberTableSearchQuery;
    },
    setRepoTableSearchQuery: (
      state: IdentitiesState,
      action: PayloadAction<string>,
    ) => {
      state.repoTableSearchQuery = action.payload;
    },
    setMemberTableSearchQuery: (
      state: IdentitiesState,
      action: PayloadAction<string>,
    ) => {
      state.memberTableSearchQuery = action.payload;
    },
    setEditRows: (
      state: IdentitiesState,
      action: PayloadAction<IdentitiesUserItemRes[]>,
    ) => {
      state.editRows = action.payload;
    },
    setHoveredRow: (state: IdentitiesState, action: PayloadAction<any>) => {
      state.hoveredRow = action.payload;
    },
    setIdentitiesDetailsTab: (
      state: IdentitiesState,
      action: PayloadAction<number>,
    ) => {
      state.identitiesDetailsTab = action.payload;
    },
  },
});

export const {
  setAccountType,
  setAccountStatus,
  setActiveSortedField,
  setActiveSortedOrder,
  setUserGroupsTeams,
  setSearchQuery,
  setRepoTableSearchQuery,
  setMemberTableSearchQuery,
  setEditRows,
  setHoveredRow,
  setIdentitiesDetailsTab,
  resetIdentitiesFilters,
} = identitiesSlice.actions;

export const identitiesReducer = identitiesSlice.reducer;
